'use client';

import React, { Suspense, useCallback, useEffect, useState } from 'react';

import { CategoryDrawerController } from '@ui/sections/categories/CategoryDrawerController';
import type { DisplayCategory } from '@ui/sections/categories/CategorySection';
import { useDrawerState } from '@website/hooks/useDrawerState';

interface CategoriesDrawerManagerProps {
  categories: DisplayCategory[];
  seeMoreText: string;
  drawerHeadingText: string;
}

/**
 * Client component that manages drawer state using URL parameters
 * This uses useSearchParams (via useDrawerState) so it needs Suspense
 */
function CategoriesDrawerManagerInner({ categories, seeMoreText, drawerHeadingText }: CategoriesDrawerManagerProps) {
  const { isOpen: urlIsOpen, setOpen: setUrlOpen } = useDrawerState('categories');
  const [isLocalOpen, setLocalOpen] = useState(urlIsOpen);

  // Sync local state with URL state
  useEffect(() => {
    setLocalOpen(urlIsOpen);
  }, [urlIsOpen]);

  // Handle drawer state changes
  const handleDrawerChange = useCallback(
    (open: boolean) => {
      setLocalOpen(open);
      setUrlOpen(open);
    },
    [setUrlOpen]
  );

  // Handle escape key to close drawer
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isLocalOpen) {
        handleDrawerChange(false);
      }
    };

    if (isLocalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isLocalOpen, handleDrawerChange]);

  return (
    <CategoryDrawerController
      categories={categories}
      seeMoreText={seeMoreText}
      drawerHeadingText={drawerHeadingText}
      isOpen={isLocalOpen}
      onOpenChange={handleDrawerChange}
    />
  );
}

/**
 * Wrapper with Suspense boundary for SSG compatibility
 * Only this component (which uses useSearchParams) needs Suspense
 * The CategorySection with static categories remains pure SSG
 */
export function CategoriesDrawerManager(props: CategoriesDrawerManagerProps) {
  return (
    <Suspense fallback={<span className="text-sm text-primary">{props.seeMoreText}</span>}>
      <CategoriesDrawerManagerInner {...props} />
    </Suspense>
  );
}
